/**
 * useAgGrid Custom Hook
 *
 * Consolidates AG Grid setup logic for consistent configuration across all tables.
 * Provides theme-aware styling, mode-specific configurations, and utility functions.
 */

import { useMemo, useCallback, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  TABLE_MODES,
  getModeHeights,
  getModeDefaultColDef,
  getAgGridCssVars,
  getAgGridSxOverrides,
  getAlternatingRowStyle,
  getFinancialRowStyle,
  getFinancialRowHeight,
  getAgGridThemeClass,
} from '../constants/agGridStyles';
import { getModeConfig } from '../constants/agGridModes';
import { calculateFieldStatistics } from '../utils/statistics';
import { calculateAgGridTableWidth } from '../utils/agGridSizing';

/**
 * Custom hook for AG Grid configuration
 *
 * @param {Object} options - Configuration options
 * @param {string} options.mode - Table mode (TABLE_MODES.COMPSHEET | TABLE_MODES.FINANCIAL | TABLE_MODES.INDEX)
 * @param {Array} options.numericalFields - Fields for Mean/Median calculations (CompSheet mode)
 * @param {boolean} options.showPinnedStats - Whether to show Mean/Median pinned rows
 * @param {Object} options.cssVarOverrides - Custom CSS variable overrides
 * @param {Object} options.sxOverrides - Custom sx prop overrides
 * @returns {Object} AG Grid configuration and utilities
 */
const useAgGrid = ({
  mode = TABLE_MODES.COMPSHEET,
  numericalFields = [],
  showPinnedStats = true,
  cssVarOverrides = {},
  sxOverrides = {},
} = {}) => {
  const theme = useTheme();
  const gridRef = useRef(null);

  // Get mode-specific configuration
  const modeConfig = useMemo(() => getModeConfig(mode, theme), [mode, theme]);
  const heights = useMemo(() => getModeHeights(mode), [mode]);

  // Theme class for AG Grid
  const themeClass = useMemo(() => getAgGridThemeClass(theme), [theme]);

  // CSS variables for style prop
  const cssVars = useMemo(
    () => ({
      ...getAgGridCssVars(theme, mode),
      ...cssVarOverrides,
    }),
    [theme, mode, cssVarOverrides]
  );

  // MUI sx overrides
  const sx = useMemo(
    () => ({
      ...getAgGridSxOverrides(theme, mode),
      ...sxOverrides,
    }),
    [theme, mode, sxOverrides]
  );

  // Default column definition
  const defaultColDef = useMemo(() => getModeDefaultColDef(mode), [mode]);

  // Row style function based on mode
  const getRowStyle = useMemo(() => {
    switch (mode) {
      case TABLE_MODES.FINANCIAL:
        return getFinancialRowStyle(theme);
      case TABLE_MODES.INDEX:
      case TABLE_MODES.COMPSHEET:
      default:
        return getAlternatingRowStyle(theme);
    }
  }, [mode, theme]);

  // Row height function (for financial mode with value/calculation rows)
  const getRowHeight = useMemo(() => {
    if (mode === TABLE_MODES.FINANCIAL) {
      return getFinancialRowHeight(heights);
    }
    return undefined; // Use default row height
  }, [mode, heights]);

  // Pinned bottom row data state for Mean/Median
  const [pinnedBottomRowData, setPinnedBottomRowData] = useState([
    { 'Company Name': 'Mean' },
    { 'Company Name': 'Median' },
  ]);

  // Function to calculate and update pinned bottom rows
  const updatePinnedBottomRows = useCallback(() => {
    if (!gridRef.current?.api || !showPinnedStats || numericalFields.length === 0) {
      return;
    }

    const gridApi = gridRef.current.api;
    const filteredRows = [];

    gridApi.forEachNodeAfterFilter((node) => {
      if (node.data) {
        filteredRows.push(node.data);
      }
    });

    const { mean, median } = calculateFieldStatistics(filteredRows, numericalFields);

    setPinnedBottomRowData([
      { 'Company Name': 'Mean', ...mean },
      { 'Company Name': 'Median', ...median },
    ]);
  }, [showPinnedStats, numericalFields]);

  // Grid ready handler
  const onGridReady = useCallback(
    (params) => {
      gridRef.current = params;
      if (mode === TABLE_MODES.COMPSHEET && showPinnedStats) {
        updatePinnedBottomRows();
      }
    },
    [mode, showPinnedStats, updatePinnedBottomRows]
  );

  // Filter changed handler
  const onFilterChanged = useCallback(
    (params) => {
      gridRef.current = params;
      if (mode === TABLE_MODES.COMPSHEET && showPinnedStats) {
        updatePinnedBottomRows();
      }
    },
    [mode, showPinnedStats, updatePinnedBottomRows]
  );

  // Sort changed handler
  const onSortChanged = useCallback((params) => {
    gridRef.current = params;
  }, []);

  // Get grid API
  const getGridApi = useCallback(() => {
    return gridRef.current?.api;
  }, []);

  // Get column API
  const getColumnApi = useCallback(() => {
    return gridRef.current?.columnApi;
  }, []);

  // Export filtered data
  const exportFilteredData = useCallback(() => {
    const api = getGridApi();
    if (!api) return [];

    const filteredRows = [];
    api.forEachNodeAfterFilter((node) => {
      if (node.data) {
        filteredRows.push(node.data);
      }
    });
    return filteredRows;
  }, [getGridApi]);

  // Calculate table width from column definitions
  const calculateTableWidth = useCallback((columnDefs) => (
    calculateAgGridTableWidth(columnDefs, {
      widthBuffer: 6,
      reserveScrollbar: false,
    })
  ), []);

  // Calculate table height based on row count
  const calculateTableHeight = useCallback(
    (rowCount, includeStats = true, maxHeight = 800) => {
      const headerHeight = heights.headerHeight;
      const rowHeight = heights.rowHeight;
      const statsRows = includeStats && mode === TABLE_MODES.COMPSHEET ? 2 : 0;
      const totalRows = rowCount + statsRows;
      const calculatedHeight = headerHeight + totalRows * rowHeight + 10;
      return Math.min(calculatedHeight, maxHeight);
    },
    [heights, mode]
  );

  // Base grid props that should be applied to all grids
  const baseGridProps = useMemo(
    () => ({
      domLayout: 'normal',
      suppressScrollOnNewData: true,
      enableCellTextSelection: true,
      rowHeight: heights.rowHeight,
      headerHeight: heights.headerHeight,
      // Row buffer for virtualization performance - renders extra rows outside viewport
      rowBuffer: 10,
    }),
    [heights]
  );

  // Complete grid props including event handlers
  const gridProps = useMemo(
    () => ({
      ...baseGridProps,
      ...modeConfig.gridProps,
      defaultColDef,
      getRowStyle,
      getRowHeight: mode === TABLE_MODES.FINANCIAL ? getRowHeight : undefined,
      onGridReady,
      onFilterChanged,
      onSortChanged,
    }),
    [
      baseGridProps,
      modeConfig.gridProps,
      defaultColDef,
      getRowStyle,
      getRowHeight,
      mode,
      onGridReady,
      onFilterChanged,
      onSortChanged,
    ]
  );

  return {
    // Refs
    gridRef,

    // Configuration
    mode,
    modeConfig,
    heights,
    themeClass,
    cssVars,
    sx,
    defaultColDef,

    // Row styling
    getRowStyle,
    getRowHeight,

    // Pinned rows (CompSheet mode)
    pinnedBottomRowData: showPinnedStats ? pinnedBottomRowData : [],
    updatePinnedBottomRows,

    // Event handlers
    onGridReady,
    onFilterChanged,
    onSortChanged,

    // Utilities
    getGridApi,
    getColumnApi,
    exportFilteredData,
    calculateTableWidth,
    calculateTableHeight,

    // Complete grid props for spreading
    gridProps,
    baseGridProps,
  };
};

export default useAgGrid;
